import aa from 'search-insights'
import { AlgoliaClient } from '@/search/algolia_client'
import {
  FACET_GRADES,
  generateAlgoliaFacetFiltersFromBrowseFilters,
  generateAlgoliaFiltersFromBrowseFilters
} from '@/search/browse_filters'
import type { BrowseFilters } from '@/search/browse_filters'
import type { AlgoliaIndices } from '@/search/search_types'

const appID: string = import.meta.env.VITE_ALGOLIA_APP_ID
export const GOALS_INDEX: string = import.meta.env.VITE_ALGOLIA_GOALS_INDEX
export const ASSESSMENTS_INDEX: string = import.meta.env.VITE_ALGOLIA_ALL_ASSESSMENTS_INDEX
// Browsing assessments without a keyword reads this replica, which orders them by sort key.
export const ASSESSMENTS_SORTED_INDEX = `${ASSESSMENTS_INDEX}_sort_key_asc`

export const CLICKED_GOAL_TILE_EVENT = 'Clicked Goal Tile'

type Hit = Record<string, unknown>

// Created on first use and shared by every caller. A failed creation is forgotten so the
// next call tries again rather than inheriting the rejection.
function lazySharedClient(featureName: string, indices: AlgoliaIndices, { sendsEvents }: { sendsEvents: boolean }) {
  let clientPromise: Promise<AlgoliaClient> | null = null
  const create = async () => {
    const client = await AlgoliaClient.create({ appID, featureName, indices })
    if (sendsEvents) {
      await client.initAnalytics()
    }
    return client
  }
  return () => {
    clientPromise ??= create().catch((e) => {
      clientPromise = null
      throw e
    })
    return clientPromise
  }
}

// Insights holds a single key, so only the client whose index receives events initialises it.
export const getBrowseGoalsClient = lazySharedClient('browse_goals', GOALS_INDEX, { sendsEvents: true })
const getAssessmentsClient = lazySharedClient('browse_assessments', ASSESSMENTS_INDEX, { sendsEvents: false })
// Sends its Insights events with its own key on each event, leaving the shared Insights key to Browse Goals.
export const getBrowseAssessmentsClient = lazySharedClient('browse_assessments', [ASSESSMENTS_INDEX, ASSESSMENTS_SORTED_INDEX], { sendsEvents: false })

export async function getGradesWithResultsForFilters(filters: BrowseFilters, standardSets: string[]): Promise<string[]> {
  const client = await getBrowseGoalsClient()
  const response = await client.search('', {
    facetFilters: generateAlgoliaFacetFiltersFromBrowseFilters(filters, standardSets),
    filters: generateAlgoliaFiltersFromBrowseFilters(filters),
    facets: [FACET_GRADES],
    responseFields: ['nbHits', 'facets']
  })
  return Object.keys(response.facets?.[FACET_GRADES] ?? {})
}

export async function searchAssessmentsByTitle<THit = Hit>(title: string, grades: string[] = []): Promise<THit[]> {
  const client = await getAssessmentsClient()
  const facetFilters = grades.length ? [grades.map((grade) => `grades:${grade}`)] : []
  const response = await client.search<THit>(title, { facetFilters, responseFields: ['hits', 'nbHits'] })
  if (!Array.isArray(response?.hits)) {
    console.warn('Unexpected Algolia assessments response shape', { nbHits: response?.nbHits })
    return []
  }
  return response.hits
}

export interface ClickedGoalResult {
  userToken?: string
  queryID: string
  objectIDs: string[]
  positions: number[]
}

export async function sendAlgoliaClickedBrowseGoalResultEvent({ userToken, queryID, objectIDs, positions }: ClickedGoalResult) {
  const client = await getBrowseGoalsClient()
  aa('clickedObjectIDsAfterSearch', {
    userToken: userToken || client.getPlaceholderUserToken(),
    eventName: CLICKED_GOAL_TILE_EVENT,
    index: GOALS_INDEX,
    objectIDs,
    positions,
    queryID
  })
}

export interface AssessmentResultInteraction {
  eventName: string
  userToken: string
  index: string
  queryID: string
  objectID: string
  position: number
}

export interface ViewedAssessmentResults {
  userToken: string
  index: string
  objectIDs: string[]
}

export interface AppliedAssessmentFilter {
  userToken: string
  index: string
  filter: string
}

// Keeps the name Browse Assessments' view events already carry, so the Insights series continues;
// Algolia accepts at most 20 object IDs in each.
const VIEWED_ASSESSMENT_RESULTS_EVENT = 'Hits Viewed'
const MAX_OBJECT_IDS_PER_EVENT = 20
// Browse Assessments' Insights history was recorded through instantsearch, which names each filter
// chosen this way and tags every event with its source, views as its own internal ones; keeping
// the names and tags keeps the Insights series and its source breakdown continuous.
const APPLIED_FILTER_EVENT = 'Filter Applied'
const INSTANTSEARCH_SOURCE = { algoliaSource: ['instantsearch'] }
const INSTANTSEARCH_VIEW_SOURCE = { algoliaSource: ['instantsearch', 'instantsearch-internal'] }

type InsightsCredentials = { headers: Record<string, string> }

// Insights events are best-effort and their callers fire and forget, so a failure is reported here
// rather than surfacing as an unhandled rejection.
async function sendWithBrowseAssessmentsKey(send: (credentials: InsightsCredentials) => Promise<unknown>[]) {
  try {
    const client = await getBrowseAssessmentsClient()
    const credentials = { headers: { 'X-Algolia-Application-Id': appID, 'X-Algolia-API-Key': await client.getAPIKey() } }
    await Promise.all(send(credentials))
  } catch (error) {
    console.warn('Failed to send Browse Assessments Insights event', error)
  }
}

export function sendAlgoliaClickedAssessmentResultEvent({ eventName, userToken, index, queryID, objectID, position }: AssessmentResultInteraction) {
  return sendWithBrowseAssessmentsKey((credentials) => [
    aa('clickedObjectIDsAfterSearch', { eventName, userToken, index, queryID, objectIDs: [objectID], positions: [position], ...INSTANTSEARCH_SOURCE }, credentials)
  ])
}

export function sendAlgoliaConvertedAssessmentResultEvent({ eventName, userToken, index, queryID, objectID }: AssessmentResultInteraction) {
  return sendWithBrowseAssessmentsKey((credentials) => [
    aa('convertedObjectIDsAfterSearch', { eventName, userToken, index, queryID, objectIDs: [objectID], ...INSTANTSEARCH_SOURCE }, credentials)
  ])
}

function inBatches(objectIDs: string[]): string[][] {
  const batches: string[][] = []
  for (let start = 0; start < objectIDs.length; start += MAX_OBJECT_IDS_PER_EVENT) {
    batches.push(objectIDs.slice(start, start + MAX_OBJECT_IDS_PER_EVENT))
  }
  return batches
}

export function sendAlgoliaViewedAssessmentResultsEvents({ userToken, index, objectIDs }: ViewedAssessmentResults) {
  return sendWithBrowseAssessmentsKey((credentials) =>
    inBatches(objectIDs).map((batch) =>
      aa('viewedObjectIDs', { eventName: VIEWED_ASSESSMENT_RESULTS_EVENT, userToken, index, objectIDs: batch, ...INSTANTSEARCH_VIEW_SOURCE }, credentials)
    )
  )
}

export function sendAlgoliaAppliedAssessmentFilterEvent({ userToken, index, filter }: AppliedAssessmentFilter) {
  return sendWithBrowseAssessmentsKey((credentials) => [
    aa('clickedFilters', { eventName: APPLIED_FILTER_EVENT, userToken, index, filters: [filter], ...INSTANTSEARCH_SOURCE }, credentials)
  ])
}
